import React from 'react'
import { useFormContext } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { roundedPrice } from '../../../../../../../utils'
import { HTLabel, Icon } from '../../../../../../atoms'
import { Divider } from '../../../../../../atoms/divider/Divider'
import {
  ControlledSwitchField,
  ControlledTextField,
} from '../../../../../../forms'
import { formatMoney } from '../../../../../house-options/shared/utils/format/format-money/format-money'
import { useBigCalencarFormContext } from '../../../shared/calendar/utils/use-big-calendar-form'

type BigCalendarModalBodyIndividualProps = {
  isFormDisabled: boolean
}
export const BigCalendarModalBodyIndividual = ({
  isFormDisabled,
}: BigCalendarModalBodyIndividualProps) => {
  const intl = useIntl()
  const { modalFormProps } = useBigCalencarFormContext()

  /**
   * This is voluntarily not type, as RHF cannot handle the deps of type
   * - Would make Intellisense infinite loop
   * - Should be `useFormContext<BigCalendarModalFormProps>()`
   */
  const { control, watch, setValue } = useFormContext()

  if (!modalFormProps || modalFormProps.kind !== 'individual') {
    return (
      <div className='flex h-full w-full items-center justify-center gap-4'>
        <Icon name='cross-without-circle' size={20} color='var(--red-600)' />
        Une erreur est survenue lors de l&apos;affichage du formulaire...
      </div>
    )
  }

  const { nightlyTrip, studyDay, house } = modalFormProps.formValues

  const isNightlyTripSwitchEnabled = watch('formValues.nightlyTrip.isEnabled')

  const isStudyDaySwitchEnabled = watch('formValues.studyDay.isEnabled')

  return (
    <div className='h-full w-full p-4'>
      {house.options.nightlyTrip.isEnabled ? (
        <div className='flex flex-col'>
          <div className='relative flex items-center justify-center'>
            <ControlledSwitchField
              label={intl.formatMessage({
                defaultMessage:
                  'Le logement est-il disponible à la nuitée à cette date ?',
                id: 'ui.BigCalendarModalBodyIndividual.form.nightlyTripAvailable',
              })}
              name='formValues.nightlyTrip.isEnabled'
              control={control}
              disabled={isFormDisabled}
              inline
            />
          </div>

          {isNightlyTripSwitchEnabled ? (
            <>
              <ControlledTextField
                label={
                  <>
                    <FormattedMessage
                      id='ui.BigCalendarModalBodyIndividual.PricePerPersonPerNight'
                      defaultMessage='Prix par personne et par nuit en <strong>chambre individuelle</strong>'
                      values={{
                        strong: (chunks) => <strong>{chunks}</strong>,
                      }}
                    />
                    <HTLabel />
                  </>
                }
                type='number'
                control={control}
                inline
                name='formValues.nightlyTrip.options.singleBedroomPrice'
                formatValue={(value) =>
                  formatMoney({ value, currency: house.currencyArea.currency })
                }
                onReset={() =>
                  setValue(
                    'formValues.nightlyTrip.options.singleBedroomPrice',
                    null,
                    { shouldDirty: true },
                  )
                }
                placeholder={
                  nightlyTrip.options?.singleBedroomPrice
                    ? roundedPrice(nightlyTrip.options?.singleBedroomPrice, {
                        currency: house.currencyArea.currency,
                      })
                    : intl.formatMessage({
                        defaultMessage: 'Écrire ici',
                        id: 'ui.BigCalendarModalBodyIndividual.writeHere',
                      })
                }
                disabled={isFormDisabled}
              />

              <ControlledTextField
                label={
                  <>
                    <FormattedMessage
                      id='ui.BigCalendarModalBodyIndividual.priceByPersonAndByNight'
                      defaultMessage='Prix par personne et par nuit en <strong>chambre partagée</strong>'
                      values={{
                        strong: (chunks) => <strong>{chunks}</strong>,
                      }}
                    />{' '}
                    <HTLabel />
                  </>
                }
                type='number'
                control={control}
                inline
                name='formValues.nightlyTrip.options.sharedBedroomPrice'
                formatValue={(value) =>
                  formatMoney({ value, currency: house.currencyArea.currency })
                }
                onReset={() =>
                  setValue(
                    'formValues.nightlyTrip.options.sharedBedroomPrice',
                    null,
                    { shouldDirty: true },
                  )
                }
                placeholder={
                  nightlyTrip.options?.sharedBedroomPrice
                    ? roundedPrice(nightlyTrip.options?.sharedBedroomPrice, {
                        currency: house.currencyArea.currency,
                      })
                    : intl.formatMessage({
                        defaultMessage: 'Écrire ici',
                        id: 'ui.BigCalendarModalBodyIndividual.writeHere',
                      })
                }
                disabled={isFormDisabled}
              />
            </>
          ) : null}

          <Divider />
        </div>
      ) : null}

      {house.options.studyDay.isEnabled ? (
        <div className='flex flex-col'>
          <div className='relative flex items-center justify-center'>
            <ControlledSwitchField
              label={intl.formatMessage({
                id: 'ui.BigCalendarModalBodyIndividual.modal.studyDayAvailable',
                defaultMessage:
                  'Le logement est-il disponible à la journée à cette date ?',
              })}
              name='formValues.studyDay.isEnabled'
              control={control}
              disabled={isFormDisabled}
              inline
            />
          </div>

          {isStudyDaySwitchEnabled ? (
            <>
              <ControlledTextField
                label={
                  <>
                    <FormattedMessage
                      id='ui.BigCalendarModalBodyIndividual.priceWorkspacesResidential'
                      defaultMessage='Tarif accès aux salles pour le forfait <strong>séminaire résidentiel</strong>'
                      values={{
                        strong: (chunks) => <strong>{chunks}</strong>,
                      }}
                    />{' '}
                    <HTLabel />
                  </>
                }
                control={control}
                type='number'
                inline
                name='formValues.studyDay.options.residentialPrice'
                formatValue={(value) =>
                  formatMoney({ value, currency: house.currencyArea.currency })
                }
                onReset={() =>
                  setValue(
                    'formValues.studyDay.options.residentialPrice',
                    null,
                    { shouldDirty: true },
                  )
                }
                placeholder={
                  studyDay.options?.residentialPrice
                    ? roundedPrice(studyDay.options?.residentialPrice, {
                        currency: house.currencyArea.currency,
                      })
                    : intl.formatMessage({
                        defaultMessage: 'Écrire ici',
                        id: 'ui.BigCalendarModalBodyIndividual.writeHere',
                      })
                }
                disabled={isFormDisabled}
              />

              <ControlledTextField
                label={
                  <>
                    <FormattedMessage
                      id='ui.BigCalendarModalBodyIndividual.priceWorkspacesDaily'
                      defaultMessage="Tarif accès aux salles pour le forfait <strong>journée d'étude</strong>"
                      values={{
                        strong: (chunks) => <strong>{chunks}</strong>,
                      }}
                    />{' '}
                    <HTLabel />
                  </>
                }
                control={control}
                type='number'
                inline
                name='formValues.studyDay.options.fullDayPrice'
                formatValue={(value) =>
                  formatMoney({ value, currency: house.currencyArea.currency })
                }
                onReset={() =>
                  setValue('formValues.studyDay.options.fullDayPrice', null, {
                    shouldDirty: true,
                  })
                }
                placeholder={
                  studyDay.options?.fullDayPrice
                    ? roundedPrice(studyDay.options?.fullDayPrice, {
                        currency: house.currencyArea.currency,
                      })
                    : intl.formatMessage({
                        defaultMessage: 'Écrire ici',
                        id: 'ui.BigCalendarModalBodyIndividual.writeHere',
                      })
                }
                disabled={isFormDisabled}
              />

              <ControlledTextField
                label={
                  <>
                    <FormattedMessage
                      id='ui.BigCalendarModalBodyIndividual.priceWorkspacesHalfDay'
                      defaultMessage="Tarif accès aux salles pour le forfait <strong>1/2 journée d'étude</strong>"
                      values={{
                        strong: (chunks) => <strong>{chunks}</strong>,
                      }}
                    />{' '}
                    <HTLabel />
                  </>
                }
                control={control}
                type='number'
                inline
                name='formValues.studyDay.options.halfDayPrice'
                formatValue={(value) =>
                  formatMoney({ value, currency: house.currencyArea.currency })
                }
                onReset={() =>
                  setValue('formValues.studyDay.options.halfDayPrice', null, {
                    shouldDirty: true,
                  })
                }
                placeholder={
                  studyDay.options?.halfDayPrice
                    ? roundedPrice(studyDay.options?.halfDayPrice, {
                        currency: house.currencyArea.currency,
                      })
                    : intl.formatMessage({
                        defaultMessage: 'Écrire ici',
                        id: 'ui.BigCalendarModalBodyIndividual.writeHere',
                      })
                }
                disabled={isFormDisabled}
              />
            </>
          ) : null}

          <Divider />
        </div>
      ) : null}

      {house.options.corporateEvening.isEnabled ? (
        <div className='flex flex-col'>
          <div className='relative flex items-center justify-center'>
            <ControlledSwitchField
              label={intl.formatMessage({
                id: 'ui.BigCalendarModalBodyIndividual.modal.corporateEveningAvailable',
                defaultMessage:
                  "Le logement est-disponible pour une soirée d'entreprise à cette date ?",
              })}
              name='formValues.corporateEvening.isEnabled'
              control={control}
              disabled={isFormDisabled}
              inline
            />
          </div>
          <Divider />
        </div>
      ) : null}
    </div>
  )
}
